import React, { useContext, useState, useEffect } from "react";
import { Button, Card, CardBody, CardHeader, Col, Form, FormGroup, Input, Label, Row, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { PostData } from "../../../services/FetchData";
import { ProductContext } from "../../../store";
import IndexDropdown from "../../../components/Drupal/IndexDropdown";

const DRUPAL_BASE = "ms3";

const CustomRanking = () => {
	const { currentWebsiteId, websiteData } = useContext(ProductContext);
	const [file, setFile] = useState(null);
	const [uploading, setUploading] = useState(false);
	const [indexIds, setIndexIds] = useState([]);
	const [productIndexId, setProductIndexId] = useState("");
	const [loadingIndexes, setLoadingIndexes] = useState(false);

	const platform = websiteData?.[currentWebsiteId]?.websiteType;
	const isDrupal = platform === "drupal";

	// Load index list the same way as SearchResultSettings (autocomplete/config)
	useEffect(() => {
		if (!currentWebsiteId || !isDrupal) return;
		setLoadingIndexes(true);
		PostData(DRUPAL_BASE, "api/v1/drupal/autocomplete/config", { websiteId: currentWebsiteId })
			.then((res) => {
				const ids = res?.indexConfig?.indexIds || [];
				setIndexIds(Array.isArray(ids) && ids.length ? ids : ["commerce_products"]);
				if (Array.isArray(ids) && ids.length && !ids.includes(productIndexId)) setProductIndexId(ids[0]);
			})
			.catch(() => {
				setIndexIds(["commerce_products"]);
				setProductIndexId("commerce_products");
			})
			.finally(() => setLoadingIndexes(false));
	}, [currentWebsiteId, isDrupal]); // eslint-disable-line react-hooks/exhaustive-deps -- productIndexId intentionally not in deps

	const handleFileChange = (e) => {
		const f = e.target.files?.[0];
		if (f) {
			if (!f.name.toLowerCase().endsWith(".xlsx")) {
				toast.error("Please select an .xlsx file");
				setFile(null);
				return;
			}
			setFile(f);
		} else {
			setFile(null);
		}
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		if (!currentWebsiteId || !file) {
			toast.error("Please select a website and an .xlsx file");
			return;
		}
		if (!productIndexId) {
			toast.error("Please select which collection to update");
			return;
		}
		setUploading(true);
		const formData = new FormData();
		formData.append("file", file);
		formData.append("websiteId", String(currentWebsiteId));
		formData.append("productIndexId", productIndexId);
		PostData(DRUPAL_BASE, "api/v1/drupal/top-product-list/upload", formData, true)
			.then((res) => {
				if (res?.status === "success") {
					toast.success(res?.message || "Top product list uploaded successfully");
					setFile(null);
					if (e.target?.reset) e.target.reset();
				} else {
					toast.error(res?.message || "Upload failed");
				}
			})
			.catch((err) => {
				toast.error(err?.message || "Upload failed");
			})
			.finally(() => setUploading(false));
	};

	if (!isDrupal) {
		return (
			<div className="p-4">
				<Card>
					<CardBody>This page is available for Drupal websites only.</CardBody>
				</Card>
			</div>
		);
	}

	return (
		<div className="p-4">
			<Card>
				<CardHeader>
					<h5 className="mb-0">Top Product List (Custom ranking)</h5>
				</CardHeader>
				<CardBody>
					<p className="text-muted mb-4">
						Select which product collection to update, then upload an Excel (.xlsx) file with SKUs and optional rank. Custom rank is written only to the selected collection.
					</p>
					<Form onSubmit={handleSubmit}>
						<Row>
							<Col md={8}>
								<FormGroup>
									<Label for="productIndexId">Collection to update</Label>
									{loadingIndexes ? (
										<div className="text-muted small">Loading collections…</div>
									) : (
										<IndexDropdown
											indexIds={indexIds}
											value={productIndexId}
											onChange={setProductIndexId}
											placeholder="Select index for custom ranking..."
										/>
									)}
								</FormGroup>
							</Col>
						</Row>
						<Row>
							<Col md={8}>
								<FormGroup>
									<Label for="customRankingFile">Excel file (.xlsx)</Label>
									<Input
										id="customRankingFile"
										type="file"
										accept=".xlsx"
										onChange={handleFileChange}
										disabled={uploading}
									/>
								</FormGroup>
							</Col>
						</Row>
						<Button type="submit" color="primary" disabled={!file || uploading || !productIndexId || loadingIndexes}>
							{uploading ? <Spinner size="sm" className="me-2" /> : null}
							{uploading ? "Uploading…" : "Upload"}
						</Button>
					</Form>
				</CardBody>
			</Card>
		</div>
	);
};

export default CustomRanking;
